/**
 * Detalle de Conduce x Mayor — donde el conduce se convierte en factura.
 *
 * Desde una sola pantalla:
 *   - Marcar (y desmarcar) los chasis que el cliente reportó como vendidos.
 *   - Cambiar el precio pactado de un chasis mientras no se haya facturado.
 *   - Facturar los marcados: una factura x mayor normal por tanda, con su
 *     comprobante, su condición y su plan de cuotas.
 *   - Devolver al inventario los motores que quedaron en piso (por fila o en
 *     lote desde la selección). "Desmarcar vendidos" NO devuelve nada: solo
 *     deshace la marca de vendido y el motor sigue en el cliente.
 *   - Anular el conduce completo si todavía no ha facturado nada.
 *   - Consultar las facturas que salieron del conduce. Esas facturas NO están
 *     en el listado de Facturas x Mayor: este es su único acceso, y cada una
 *     abre el detalle de factura de siempre (imprimir, cobrar, anular, precios).
 *
 * Permisos: marcar/facturar exigen "modificar" y devolver/anular "borrar" del
 * programa conducesxmayor (el backend re-valida todos).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import {
  FP,
  StatusPill,
  fpInputSx,
  fpDataGridSx,
  RazonCancelacionField,
} from "components/financepro";
import { tienePermisosModulo, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import { precioEnMoneda } from "lib/moneda";
import { estadoConduce } from "lib/conducesxmayor";
import FacturaDetalleXmayor from "modules/ventasxmayor/facturadetallexmayor";
import { fmtMoney, monedaLabel, imprimirConduce } from "modules/ventasxmayor/impresionxmayor";

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const round2 = (n) => Math.round(n * 100) / 100;

const hoyISO = () => new Date().toISOString().slice(0, 10);

const fmtFechaISO = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const hoyMasDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return fmtFechaISO(d);
};

// Suma meses a una fecha "YYYY-MM-DD" ajustando al último día del mes destino.
const sumarMeses = (iso, meses) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, m - 1 + meses, 1);
  const ultimo = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
  dt.setDate(Math.min(d, ultimo));
  return fmtFechaISO(dt);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const tabSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.85rem",
  minHeight: 40,
  px: 2,
  borderRadius: 999,
  border: `1px solid ${FP.color.border}`,
  color: FP.color.textSecondary,
  bgcolor: "#FFFFFF",
  "& .MuiSvgIcon-root": { color: "inherit" },
  "&:hover": { borderColor: "#0B1220", color: "#0B1220" },
  "&.MuiTab-root.Mui-selected": {
    bgcolor: "#0B1220",
    borderColor: "#0B1220",
    color: "#FFFFFF !important",
  },
};

function Dato({ label, value, strong = false }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", color: FP.color.textMuted, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: strong ? "1.05rem" : "0.86rem",
          fontWeight: strong ? 800 : 600,
          color: FP.color.textPrimary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const CHIP_LINEA = {
  "": { label: "En piso", variant: "info" },
  V: { label: "Vendido", variant: "warning" },
  F: { label: "Facturado", variant: "success" },
  D: { label: "Devuelto", variant: "neutral" },
};

export default function ConduceDetalleXmayor({ conduceId, onExit, onChanged } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Por programa, no por categoría: es como valida permisos.py.
  const puedeVer = tienePermisosModulo(roleDetalles, "conducesxmayor");
  const puedeModificar = puedeAccionPrograma(roleDetalles, "conducesxmayor", "modificar");
  const puedeBorrar = puedeAccionPrograma(roleDetalles, "conducesxmayor", "borrar");
  // Anular una factura sigue siendo ares9720/borrar (lo que exige el backend):
  // el permiso del conduce no da poder sobre facturas ya emitidas. Y como estas
  // facturas no salen en Mantenimiento de Facturas, este es su único acceso.
  const puedeAnularFactura = puedeAccionPrograma(roleDetalles, "ares9720", "borrar");

  const [det, setDet] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_detalle", { id: conduceId });
      if (ok) {
        setDet(data);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el conduce", color: "error" });
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    post("/api/xmayor_config", {}).then(({ ok, data }) => {
      if (ok) setConfig(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conduceId]);

  const c = det?.conduce || {};
  const lineas = det?.lineas || [];
  const facturas = det?.facturas || [];
  const mon = monedaLabel(c.moneda);
  const estado = estadoConduce(c);
  const anulado = estado.clave === "anulado";

  const enPiso = useMemo(() => lineas.filter((l) => !String(l.estado || "").trim()), [lineas]);
  const vendidos = useMemo(() => lineas.filter((l) => l.estado === "V"), [lineas]);

  // ---- Marcar / desmarcar vendidos ----
  const [seleccion, setSeleccion] = useState([]);
  const [fechaventa, setFechaventa] = useState(hoyISO());
  const [marcando, setMarcando] = useState(false);

  useEffect(() => {
    setSeleccion([]);
  }, [det]);

  const selEnPiso = useMemo(
    () => enPiso.map((l) => l.chasis).filter((ch) => seleccion.includes(ch)),
    [enPiso, seleccion]
  );
  const selVendidos = useMemo(
    () => vendidos.map((l) => l.chasis).filter((ch) => seleccion.includes(ch)),
    [vendidos, seleccion]
  );

  const alternar = (chasis) => {
    setSeleccion((prev) =>
      prev.includes(chasis) ? prev.filter((x) => x !== chasis) : [...prev, chasis]
    );
  };

  const marcar = async (vendido) => {
    const chasis = vendido ? selEnPiso : selVendidos;
    if (!chasis.length) return;
    setMarcando(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_marcarvendido", {
        id: conduceId,
        chasis,
        vendido: vendido ? "S" : "N",
        fechaventa,
      });
      if (ok) {
        showAlert({ message: data.ok || "Chasis actualizados", color: "success" });
        setSeleccion([]);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo marcar", color: "error" });
      }
    } finally {
      setMarcando(false);
    }
  };

  // ---- Facturar la tanda ----
  const [factOpen, setFactOpen] = useState(false);
  const [factChasis, setFactChasis] = useState([]);
  const [factPrecios, setFactPrecios] = useState({});
  const [tipocomprobante, setTipocomprobante] = useState("");
  const [tipofactura, setTipofactura] = useState("Credito");
  const [plazos, setPlazos] = useState("1");
  const [fechaprimerpago, setFechaprimerpago] = useState(hoyMasDias(30));
  const [tasa, setTasa] = useState("");
  const [facturando, setFacturando] = useState(false);

  const abrirFacturar = () => {
    // Por defecto entra toda la tanda vendida: es el caso normal, y el usuario
    // puede destildar si quiere facturar solo parte.
    setFactChasis(vendidos.map((l) => l.chasis));
    setFactPrecios(Object.fromEntries(vendidos.map((l) => [l.chasis, toNum(l.precio).toFixed(2)])));
    setTipocomprobante(config?.tiposComprobante?.[0] || "");
    setTipofactura("Credito");
    setPlazos("1");
    setFechaprimerpago(hoyMasDias(30));
    setTasa(String(toNum(c.tasa) || config?.tasaus || ""));
    setFactOpen(true);
  };

  // Mínimo del chasis (producto.mprecio, en la moneda BASE) llevado a la
  // moneda del conduce. Sin catálogo de monedas el backend responde "DOP" y
  // el resultado es el de siempre.
  const minimoEnMoneda = (l) => precioEnMoneda(l.mprecio, c.moneda, tasa, config?.base || "DOP");

  const totalFactura = useMemo(
    () => factChasis.reduce((acc, ch) => acc + toNum(factPrecios[ch]), 0),
    [factChasis, factPrecios]
  );

  const bajoMinimo = useMemo(
    () =>
      vendidos.find(
        (l) =>
          factChasis.includes(l.chasis) &&
          minimoEnMoneda(l) > 0 &&
          toNum(factPrecios[l.chasis]) < minimoEnMoneda(l)
      ),
    [vendidos, factChasis, factPrecios, tasa]
  );

  const cuotasPreview = useMemo(() => {
    if (tipofactura !== "Credito") return [];
    const n = parseInt(plazos, 10) || 0;
    if (n < 1 || n > 60 || totalFactura <= 0 || !fechaprimerpago) return [];
    const cuota = round2(totalFactura / n);
    const filas = [];
    for (let i = 0; i < n; i += 1) {
      filas.push({
        numero: i + 1,
        fecha: sumarMeses(fechaprimerpago, i),
        valor: i === n - 1 ? round2(totalFactura - cuota * (n - 1)) : cuota,
      });
    }
    return filas;
  }, [tipofactura, plazos, totalFactura, fechaprimerpago]);

  const facturar = async () => {
    if (!factChasis.length) {
      showAlert({ message: "Seleccione al menos un chasis vendido", color: "warning" });
      return;
    }
    if (bajoMinimo) {
      showAlert({
        message: `El precio del chasis ${
          bajoMinimo.chasis
        } no puede ser menor al mínimo (${mon} ${fmtMoney(minimoEnMoneda(bajoMinimo))})`,
        color: "warning",
      });
      return;
    }
    setFacturando(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_facturar", {
        id: conduceId,
        chasis: factChasis,
        items: factChasis.map((ch) => ({ chasis: ch, precio: toNum(factPrecios[ch]) })),
        tipocomprobante,
        tipofactura,
        tasa: toNum(tasa),
        plazos: tipofactura === "Credito" ? parseInt(plazos, 10) || 1 : 1,
        fechaprimerpago,
      });
      if (ok) {
        showAlert({ message: `Factura #${data.id} registrada`, color: "success" });
        setFactOpen(false);
        cargar();
        if (onChanged) onChanged();
        // La factura recién emitida se abre en su detalle de siempre, que es de
        // donde se imprime: aquí no se duplica la impresión.
        setFacturaId(data.id);
      } else {
        showAlert({ message: data.Error || "No se pudo facturar", color: "error" });
      }
    } finally {
      setFacturando(false);
    }
  };

  // ---- Devolución y anulación ----
  const [devTarget, setDevTarget] = useState(null);
  const [devMotivo, setDevMotivo] = useState("");
  const [devolviendo, setDevolviendo] = useState(false);

  const devLista = Array.isArray(devTarget?.chasis) ? devTarget.chasis : [];

  const devolver = async () => {
    setDevolviendo(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_devolver", {
        id: conduceId,
        chasis: devTarget.chasis,
        motivo: devMotivo.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Chasis devuelto", color: "success" });
        setDevTarget(null);
        setDevMotivo("");
        setSeleccion([]);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo devolver el chasis", color: "error" });
      }
    } finally {
      setDevolviendo(false);
    }
  };

  // ---- Precio pactado de un chasis sin facturar ----
  const [precioTarget, setPrecioTarget] = useState(null);
  const [precioNuevo, setPrecioNuevo] = useState("");
  const [guardandoPrecio, setGuardandoPrecio] = useState(false);

  const abrirPrecio = (l) => {
    setPrecioTarget(l);
    setPrecioNuevo(toNum(l.precio).toFixed(2));
  };

  // Mínimo con la tasa del conduce (la del diálogo de facturar puede no
  // estar cargada todavía).
  const minimoPrecio = precioTarget
    ? precioEnMoneda(precioTarget.mprecio, c.moneda, c.tasa, config?.base || "DOP")
    : 0;

  const guardarPrecio = async () => {
    const precio = toNum(precioNuevo);
    if (precio <= 0) {
      showAlert({ message: "El precio no puede ser 0", color: "warning" });
      return;
    }
    if (minimoPrecio > 0 && precio < minimoPrecio - 0.01) {
      showAlert({
        message: `El precio no puede ser menor al mínimo del chasis (${mon} ${fmtMoney(
          minimoPrecio
        )})`,
        color: "warning",
      });
      return;
    }
    setGuardandoPrecio(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_precio", {
        id: conduceId,
        chasis: precioTarget.chasis,
        precio,
      });
      if (ok) {
        showAlert({ message: data.ok || "Precio actualizado", color: "success" });
        setPrecioTarget(null);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo cambiar el precio", color: "error" });
      }
    } finally {
      setGuardandoPrecio(false);
    }
  };

  const [anularOpen, setAnularOpen] = useState(false);
  const [anulMotivo, setAnulMotivo] = useState("");
  const [anulando, setAnulando] = useState(false);

  const anular = async () => {
    setAnulando(true);
    try {
      const { ok, data } = await post("/api/conducexmayor_anular", {
        id: conduceId,
        motivo: anulMotivo.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Conduce anulado", color: "success" });
        setAnularOpen(false);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo anular el conduce", color: "error" });
      }
    } finally {
      setAnulando(false);
    }
  };

  // ---- Detalle y anulación de una factura del conduce ----
  const [facturaId, setFacturaId] = useState(null);
  const [anulFactura, setAnulFactura] = useState(null);
  const [razonAnulFactura, setRazonAnulFactura] = useState("");
  // Cada factura que se abre para anular empieza sin razón.
  useEffect(() => setRazonAnulFactura(""), [anulFactura]);
  const [anulandoFactura, setAnulandoFactura] = useState(false);

  const anularFactura = async () => {
    setAnulandoFactura(true);
    try {
      const { ok, data } = await post("/api/xmayor_factura_anular", {
        id: anulFactura.id,
        razon: razonAnulFactura,
      });
      if (ok) {
        showAlert({ message: data.ok || "Factura anulada", color: "success" });
        setAnulFactura(null);
        cargar();
        if (onChanged) onChanged();
      } else {
        showAlert({ message: data.Error || "No se pudo anular la factura", color: "error" });
      }
    } finally {
      setAnulandoFactura(false);
    }
  };

  const columnasLineas = [
    ...(puedeModificar && !anulado
      ? [
          {
            field: "sel",
            headerName: "",
            width: 56,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
              const l = params.row;
              const marcable = !String(l.estado || "").trim() || l.estado === "V";
              return (
                <Checkbox
                  size="small"
                  disabled={!marcable}
                  checked={seleccion.includes(l.chasis)}
                  onChange={() => alternar(l.chasis)}
                />
              );
            },
          },
        ]
      : []),
    { field: "chasis", headerName: "Chasis", width: 170 },
    { field: "marca", headerName: "Marca", width: 110 },
    { field: "modelo", headerName: "Modelo", width: 110 },
    { field: "colore", headerName: "Color", width: 90 },
    {
      field: "precio",
      headerName: "Pactado",
      width: 150,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const l = params.row;
        // Lo facturado ya vive en su factura y lo devuelto salió del conduce.
        const editable =
          puedeModificar && !anulado && (!String(l.estado || "").trim() || l.estado === "V");
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
            {fmtMoney(l.precio)}
            {editable && (
              <Tooltip title="Cambiar precio pactado">
                <IconButton
                  size="small"
                  onClick={() => abrirPrecio(l)}
                  sx={{ color: FP.color.accentBlue }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "preciofacturado",
      headerName: "Facturado",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        params.row.estado === "F" ? fmtMoney(params.row.preciofacturado) : "—",
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const e = CHIP_LINEA[String(params.row.estado || "").trim()] || CHIP_LINEA[""];
        return <StatusPill label={e.label} variant={e.variant} />;
      },
    },
    {
      field: "fechaventa",
      headerName: "Vendido",
      width: 110,
      renderCell: (params) => params.row.fechaventa || "—",
    },
    {
      field: "factura",
      headerName: "Factura",
      width: 120,
      renderCell: (params) =>
        params.row.estado === "F" && params.row.factura ? (
          <Button
            size="small"
            onClick={() => setFacturaId(params.row.factura)}
            sx={{ ...btnGhost, fontWeight: 800, color: FP.color.accentBlue }}
          >
            #{params.row.factura}
          </Button>
        ) : (
          "—"
        ),
    },
    ...(puedeBorrar
      ? [
          {
            field: "acciones",
            headerName: "",
            width: 130,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
              // Solo lo que está en piso se puede devolver: lo vendido hay que
              // desmarcarlo primero y lo facturado ya no vuelve.
              if (String(params.row.estado || "").trim() || anulado) return null;
              return (
                <Button
                  size="small"
                  startIcon={<AssignmentReturnOutlinedIcon />}
                  onClick={() => setDevTarget(params.row)}
                  sx={btnOutline(FP.color.danger)}
                >
                  Devolver
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  const columnasFacturas = [
    { field: "id", headerName: "Fact#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "tipofactura", headerName: "Tipo", width: 110 },
    { field: "ncf", headerName: "NCF", flex: 1, minWidth: 140 },
    {
      // Una factura por cliente puede juntar chasis de varios conduces: aquí
      // se dice cuántos son de este y de qué otros conduces vienen los demás.
      field: "cantidad",
      headerName: "Chasis",
      width: 150,
      renderCell: (params) => {
        const r = params.row;
        const otros = r.otrosconduces || [];
        if (!otros.length) return r.cantidad;
        return (
          <Box sx={{ lineHeight: 1.2 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
              {r.delconduce} de {r.cantidad}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: FP.color.textMuted }}>
              + conduce {otros.map((x) => `#${x}`).join(", ")}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "vplicado",
      headerName: "Pagado",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "balance",
      headerName: "Balance",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setFacturaId(params.row.id)}
            sx={btnOutline(FP.color.accentBlue)}
          >
            Detalle
          </Button>
          {puedeAnularFactura && (
            <Button
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setAnulFactura(params.row)}
              disabled={params.row.anulada || toNum(params.row.vplicado) !== 0}
              sx={btnOutline(FP.color.danger)}
            >
              Anular
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  const columnasCuotas = [
    { field: "numero", headerName: "Cuota", width: 80 },
    { field: "fecha", headerName: "Fecha", flex: 1, minWidth: 110 },
    {
      field: "valor",
      headerName: "Valor",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
  ];

  if (!puedeVer) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ fontWeight: 700 }}>No tiene acceso a los conduces x mayor</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onExit}
          sx={{ mt: 2, ...btnOutline(FP.color.navy) }}
        >
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      {/* Barra superior */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          bgcolor: FP.color.cardBg,
          borderBottom: `1px solid ${FP.color.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
            Volver
          </Button>
          <Typography sx={{ fontWeight: 800, fontSize: "1.02rem", color: FP.color.textPrimary }}>
            Conduce x Mayor #{conduceId}
          </Typography>
          {det && <StatusPill label={estado.label} variant={estado.variant} />}
        </Stack>
        <Stack direction="row" spacing={1.2}>
          {puedeBorrar && det && !anulado && Number(c.facturados) === 0 && (
            <Button
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setAnularOpen(true)}
              sx={btnOutline(FP.color.danger)}
            >
              Anular conduce
            </Button>
          )}
          <Button
            startIcon={<PrintOutlinedIcon />}
            onClick={() => det && imprimirConduce(det)}
            disabled={!det}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      </Box>

      {cargando || !det ? (
        <Stack alignItems="center" sx={{ py: 10 }}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* Cabecera */}
          <Box sx={sectionBox}>
            <Grid container spacing={2.4}>
              <Grid item xs={6} sm={4} md={3}>
                <Dato label="CLIENTE" value={c.clientenombre || "—"} />
              </Grid>
              <Grid item xs={6} sm={2} md={1.5}>
                <Dato label="FECHA" value={c.fechatexto || "—"} />
              </Grid>
              <Grid item xs={4} sm={2} md={1.2}>
                <Dato label="MOTORES" value={c.motores ?? 0} />
              </Grid>
              <Grid item xs={4} sm={2} md={1.2}>
                <Dato label="EN PISO" value={c.enpiso ?? 0} strong />
              </Grid>
              <Grid item xs={4} sm={2} md={1.4}>
                <Dato label="POR FACTURAR" value={c.vendidos ?? 0} strong />
              </Grid>
              <Grid item xs={4} sm={2} md={1.3}>
                <Dato label="FACTURADOS" value={c.facturados ?? 0} />
              </Grid>
              <Grid item xs={4} sm={2} md={1.2}>
                <Dato label="DEVUELTOS" value={c.devueltos ?? 0} />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Dato label={`EN PISO ${mon}`} value={fmtMoney(c.valorpiso)} />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Dato label={`FACTURADO ${mon}`} value={fmtMoney(c.valorfacturado)} />
              </Grid>
              {c.concepto ? (
                <Grid item xs={12}>
                  <Dato label="CONCEPTO" value={c.concepto} />
                </Grid>
              ) : null}
            </Grid>
          </Box>

          {/* Acción principal: facturar lo vendido */}
          {puedeModificar && !anulado && (
            <Box
              sx={{
                ...sectionBox,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                borderColor: vendidos.length ? "#FFB74D" : FP.color.border,
                bgcolor: vendidos.length ? "#FFF8E1" : FP.color.cardBg,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                  {vendidos.length
                    ? `${vendidos.length} motor(es) vendidos por el cliente sin facturar`
                    : "Sin ventas reportadas por el cliente"}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: FP.color.textSecondary }}>
                  {vendidos.length
                    ? `Valor pactado: ${mon} ${fmtMoney(c.valorvendido)}`
                    : "Marque en la lista los chasis que el cliente reporte como vendidos"}
                </Typography>
              </Box>
              <TextField
                type="date"
                label="Fecha de venta"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={fechaventa}
                onChange={(e) => setFechaventa(e.target.value)}
                sx={{ ...fpInputSx, minWidth: 170 }}
              />
              <Button
                startIcon={marcando ? <CircularProgress size={16} color="inherit" /> : null}
                onClick={() => marcar(true)}
                disabled={marcando || !selEnPiso.length}
                sx={btnSolid("#E65100", "#BF360C")}
              >
                Marcar vendidos ({selEnPiso.length})
              </Button>
              <Button
                onClick={() => marcar(false)}
                disabled={marcando || !selVendidos.length}
                sx={btnOutline(FP.color.navy)}
              >
                Desmarcar vendidos ({selVendidos.length})
              </Button>
              {puedeBorrar && (
                <Button
                  startIcon={<AssignmentReturnOutlinedIcon />}
                  onClick={() => setDevTarget({ chasis: selEnPiso })}
                  disabled={marcando || !selEnPiso.length}
                  sx={btnOutline(FP.color.danger)}
                >
                  Devolver al inventario ({selEnPiso.length})
                </Button>
              )}
              <Button
                startIcon={<ReceiptLongOutlinedIcon />}
                onClick={abrirFacturar}
                disabled={!vendidos.length}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Facturar vendidos
              </Button>
            </Box>
          )}

          {/* Pestañas: Chasis / Facturas del conduce */}
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons={false}
              TabIndicatorProps={{ sx: { display: "none" } }}
              sx={{
                mb: 2.4,
                minHeight: 44,
                "& .MuiTabs-flexContainer": { gap: 1, width: "100%" },
                "& .MuiTab-root": { flex: { md: 1 }, maxWidth: "none" },
              }}
            >
              <Tab
                icon={<TwoWheelerOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Chasis (${lineas.length})`}
                sx={tabSx}
              />
              <Tab
                icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Facturas del conduce (${facturas.length})`}
                sx={tabSx}
              />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={lineas}
                  columns={columnasLineas}
                  getRowId={(r) => r.chasis}
                  rowHeight={46}
                  hideFooter
                  sx={fpDataGridSx}
                  disableRowSelectionOnClick
                />
                <Typography sx={{ mt: 1.2, fontSize: "0.76rem", color: FP.color.textMuted }}>
                  En piso: {enPiso.length} · Vendidos sin facturar: {vendidos.length} · Los motores
                  en piso siguen siendo de la casa: <b>Devolver al inventario</b> los saca del
                  conduce y vuelven a estar disponibles. <b>Desmarcar vendidos</b> solo deshace la
                  marca de venta.
                </Typography>
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={facturas}
                  columns={columnasFacturas}
                  getRowId={(r) => r.id}
                  rowHeight={46}
                  hideFooter
                  sx={fpDataGridSx}
                  disableRowSelectionOnClick
                />
                <Typography sx={{ mt: 1.2, fontSize: "0.76rem", color: FP.color.textMuted }}>
                  Estas facturas no salen en el listado de Facturas x Mayor: se consultan, imprimen
                  y anulan desde aquí. Sí se cobran en Cobros x Mayor y cuentan en antigüedad de
                  saldo y estado de cuenta del cliente. Al anular una, sus motores vuelven al
                  conduce como vendidos pendientes de facturar.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Facturar la tanda vendida */}
      <Dialog
        open={factOpen}
        onClose={() => setFactOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>
          Facturar lo vendido del conduce #{conduceId}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Tipo de Comprobante"
                value={tipocomprobante}
                onChange={(e) => setTipocomprobante(e.target.value)}
                helperText={
                  config && !config.ncfdisponible
                    ? "NCF no configurado — la factura se emitirá sin comprobante fiscal"
                    : undefined
                }
                sx={fpInputSx}
              >
                {(config?.tiposComprobante || []).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Condición"
                value={tipofactura}
                onChange={(e) => setTipofactura(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="Contado">Contado</MenuItem>
                <MenuItem value="Credito">Credito</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Tasa"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                helperText={`Conduce: ${fmtMoney(c.tasa)}`}
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            {tipofactura === "Credito" && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Plazos"
                    value={plazos}
                    onChange={(e) => setPlazos(e.target.value)}
                    inputProps={{ min: 1, max: 60 }}
                    helperText="1 a 60 cuotas"
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha primer pago"
                    InputLabelProps={{ shrink: true }}
                    value={fechaprimerpago}
                    onChange={(e) => setFechaprimerpago(e.target.value)}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", mb: 1 }}>
                CHASIS A FACTURAR
              </Typography>
              <Stack spacing={1}>
                {vendidos.map((l) => {
                  const min = minimoEnMoneda(l);
                  const bajo = min > 0 && toNum(factPrecios[l.chasis]) < min;
                  return (
                    <Stack
                      key={l.chasis}
                      direction="row"
                      spacing={1.2}
                      alignItems="center"
                      sx={{ flexWrap: "wrap" }}
                    >
                      <Checkbox
                        size="small"
                        checked={factChasis.includes(l.chasis)}
                        onChange={() =>
                          setFactChasis((prev) =>
                            prev.includes(l.chasis)
                              ? prev.filter((x) => x !== l.chasis)
                              : [...prev, l.chasis]
                          )
                        }
                      />
                      <Box sx={{ minWidth: 230 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.84rem" }}>
                          {l.chasis}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                          {[l.marca, l.modelo, l.colore].filter(Boolean).join(" ")}
                          {l.fechaventa ? ` · vendido ${l.fechaventa}` : ""}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        label="Precio"
                        value={factPrecios[l.chasis] ?? ""}
                        onChange={(e) =>
                          setFactPrecios((prev) => ({ ...prev, [l.chasis]: e.target.value }))
                        }
                        error={bajo}
                        helperText={min > 0 ? `Mínimo ${fmtMoney(min)}` : undefined}
                        inputProps={{ style: { textAlign: "right" } }}
                        sx={{ ...fpInputSx, width: 160 }}
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Chip
                label={`Total a facturar: ${mon} ${fmtMoney(totalFactura)} · ${
                  factChasis.length
                } chasis`}
                sx={{
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textSecondary,
                  fontWeight: 700,
                }}
              />
            </Grid>
            {cuotasPreview.length > 0 && (
              <Grid item xs={12} md={7}>
                <Box sx={{ bgcolor: "#fff" }}>
                  <DataGrid
                    autoHeight
                    rows={cuotasPreview}
                    columns={columnasCuotas}
                    getRowId={(r) => r.numero}
                    rowHeight={38}
                    hideFooter
                    sx={fpDataGridSx}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFactOpen(false)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              facturando ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ReceiptLongOutlinedIcon />
              )
            }
            onClick={facturar}
            disabled={facturando || !factChasis.length || !!bajoMinimo}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Emitir factura
          </Button>
        </DialogActions>
      </Dialog>

      {/* Devolución de un chasis en piso */}
      <Dialog
        open={!!devTarget}
        onClose={() => setDevTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>
          {devLista.length > 1
            ? `Devolver ${devLista.length} chasis al inventario`
            : "Devolver chasis"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 2 }}>
            {devLista.length > 1 ? (
              <>
                Los <b>{devLista.length}</b> chasis seleccionados vuelven al inventario disponible y
                quedan en el conduce como devueltos: <b>{devLista.join(", ")}</b>. ¿Desea continuar?
              </>
            ) : (
              <>
                El chasis <b>{devLista.length ? devLista[0] : devTarget?.chasis}</b> vuelve al
                inventario disponible y queda en el conduce como devuelto. ¿Desea continuar?
              </>
            )}
          </DialogContentText>
          <TextField
            fullWidth
            label="Motivo (opcional)"
            value={devMotivo}
            onChange={(e) => setDevMotivo(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDevTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              devolviendo ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AssignmentReturnOutlinedIcon />
              )
            }
            onClick={devolver}
            disabled={devolviendo}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, devolver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Precio pactado de un chasis sin facturar */}
      <Dialog
        open={!!precioTarget}
        onClose={() => (guardandoPrecio ? null : setPrecioTarget(null))}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>Cambiar precio</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 2 }}>
            Chasis <b>{precioTarget?.chasis}</b>
            {precioTarget?.marca ? ` · ${precioTarget.marca} ${precioTarget.modelo || ""}` : ""}. Es
            el precio que se propone al facturarlo.
            {minimoPrecio > 0 && (
              <>
                {" "}
                Mínimo del chasis: <b>{`${mon} ${fmtMoney(minimoPrecio)}`}</b>.
              </>
            )}
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            type="number"
            label={`Precio pactado (${mon})`}
            value={precioNuevo}
            onChange={(e) => setPrecioNuevo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") guardarPrecio();
            }}
            inputProps={{ min: 0, step: "0.01" }}
            disabled={guardandoPrecio}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPrecioTarget(null)} disabled={guardandoPrecio} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              guardandoPrecio ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EditOutlinedIcon />
              )
            }
            onClick={guardarPrecio}
            disabled={guardandoPrecio}
            sx={btnSolid(FP.color.accentBlue, FP.color.navy)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Anulación del conduce */}
      <Dialog
        open={anularOpen}
        onClose={() => setAnularOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular conduce</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 2 }}>
            Se devuelven al inventario los <b>{Number(c.enpiso) + Number(c.vendidos)}</b> motor(es)
            que el cliente todavía tiene y el conduce queda anulado. ¿Desea continuar?
          </DialogContentText>
          <TextField
            fullWidth
            label="Motivo (opcional)"
            value={anulMotivo}
            onChange={(e) => setAnulMotivo(e.target.value)}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAnularOpen(false)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={anular}
            disabled={anulando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Anulación de una factura del conduce */}
      <Dialog
        open={!!anulFactura}
        onClose={() => setAnulFactura(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>Anular factura</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Factura <b>{anulFactura?.id}</b> por {mon} <b>{fmtMoney(anulFactura?.valor)}</b>. Se
            borran sus cuotas y sus <b>{anulFactura?.cantidad}</b> motor(es) vuelven al conduce como{" "}
            <b>vendidos pendientes de facturar</b> — no al inventario disponible, porque el cliente
            los sigue teniendo. ¿Desea continuar?
          </DialogContentText>
          <RazonCancelacionField
            value={razonAnulFactura}
            onChange={setRazonAnulFactura}
            disabled={anulandoFactura}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAnulFactura(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              anulandoFactura ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            onClick={anularFactura}
            disabled={anulandoFactura}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, anular
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle de una factura del conduce: la pantalla de siempre */}
      <Dialog fullScreen open={!!facturaId} onClose={() => setFacturaId(null)}>
        {facturaId && (
          <FacturaDetalleXmayor
            facturaId={facturaId}
            onExit={() => {
              setFacturaId(null);
              cargar();
            }}
            onChanged={cargar}
          />
        )}
      </Dialog>
    </Box>
  );
}
